import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';

import { useMedicoesLista } from '../hooks/useMedicoes';
import type { MedicaoCicloLinha } from '../types';

const ROTULO_STATUS: Record<string, { label: string; variant: StatusVariant }> = {
  RASCUNHO: { label: 'Rascunho', variant: 'rascunho' },
  PROCESSADA: { label: 'Processada', variant: 'pendente' },
  APROVADA: { label: 'Aprovada', variant: 'aprovado' },
  EMPENHADA: { label: 'Empenhada', variant: 'aprovado' },
  LIQUIDADA: { label: 'Liquidada', variant: 'aprovado' },
  EXPORTADA_SOF: { label: 'Exportada SOF', variant: 'aprovado' },
  PAGA: { label: 'Paga', variant: 'pago' },
};

/** FIN-XXX: lista de medições mensais por credenciado. Pré-requisito de
 * navegação para a tela de detalhe (FIN-002) — regra de interface 9 do
 * CLAUDE.md exige clique de linha, nunca botão "Ver". */
export function MedicoesListPage() {
  const navigate = useNavigate();
  const [mes, definirMes] = useState('');
  const [status, definirStatus] = useState('');
  const [page, definirPage] = useState(1);
  const perPage = 20;

  const { data, isLoading, isError, refetch } = useMedicoesLista({
    mes: mes || undefined,
    status: status || undefined,
    page,
    perPage,
  });

  const campos: FilterFieldConfig[] = [
    { key: 'mes', label: 'Competência', type: 'month' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: Object.entries(ROTULO_STATUS).map(([value, { label }]) => ({ value, label })),
    },
  ];

  const colunas: DataTableColumn<MedicaoCicloLinha>[] = [
    { header: 'Credenciado', accessorKey: 'credenciadoNome' },
    {
      header: 'Competência',
      cell: (l) =>
        new Date(`${l.mesReferencia}T00:00:00`).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      header: 'Status',
      cell: (l) => {
        const tom = ROTULO_STATUS[l.status] ?? {
          label: l.status,
          variant: 'pendente' as StatusVariant,
        };
        return <StatusBadge status={tom.variant} label={tom.label} />;
      },
    },
    {
      header: 'Valor total',
      cell: (l) => <MoneyDisplay value={l.valorTotal} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financeiro"
        title="Medições"
        description="Medições financeiras consolidadas por competência e credenciado."
      />

      <FilterBar
        fields={campos}
        values={{ mes, status }}
        onChange={(chave, valor) => {
          if (chave === 'mes') definirMes(valor);
          if (chave === 'status') definirStatus(valor);
          definirPage(1);
        }}
        onClear={() => {
          definirMes('');
          definirStatus('');
          definirPage(1);
        }}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={colunas}
          data={data?.dados ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={definirPage}
          onRowClick={(l) => navigate(`/operacao/financeiro/medicoes/${l.id}`)}
          emptyTitle="Nenhuma medição encontrada"
          emptyDescription="Ajuste a competência ou o status selecionado."
          ariaLabel="Medições financeiras"
        />
      )}
    </div>
  );
}

export default MedicoesListPage;
